import React, {useContext, useEffect, useState} from "react"
import {Form, Formik, FormikHelpers, useFormikContext} from "formik"
import {useTranslation} from "next-i18next"
import CheckoutContext from "../../context/CheckoutContext"
import InputWithIcon from "../reusable/components/InputWithIcon"
import Typography from "../reusable/Typography/Typography"
import CartContext from "../../context/CartContext"
import {ArrowRight, Check, X} from "react-bootstrap-icons"
import {gaCategories, gaEvents} from "../../config/googleAnalytics/events"
import AnalyticsContext from "../../context/AnalyticsContext"
import AccordionAnimation from "../reusable/components/AnimationComponent/AccordionAnimation"
import Image from "next/image"
import {customPlanLengthMap} from "../bikeDetails/plan/customPlanLengthMap"
import OrganizationContext from "../../context/OrganizationContext"
import DiscountCodeContext from "../../context/DiscountCodeContext"

interface Values {
    discountCode: string;
}

interface Props {
	newForm?: boolean
}

const AutoSubmit = () => {
	// Grab values and submitForm from context
	const {values, submitForm} = useFormikContext()
	const {data} = useContext(CheckoutContext)
	const [timeoutId, setTimeoutId] = useState<number | null>(null)
	useEffect(() => {
		// @ts-ignore
		if (values.discountCode.length) {
			if (!timeoutId) {
				const _timeoutId = window.setTimeout(async () => {
					await submitForm()
					window.clearTimeout(timeoutId)
					setTimeoutId(null)
				}, 500)
				setTimeoutId(_timeoutId)
			}
		}
	}, [data.planLength])
	return null
}

const DiscountForm: React.FC<Props> = ({newForm}) => {
	const {t} = useTranslation()
	const {data, setData} = useContext(CheckoutContext)
	const {discountCode: discountCodeContext, setDiscountCode} = useContext(DiscountCodeContext)
	const {organizationSlug, organizationInitialized, organizationData} = useContext(OrganizationContext)
	const {items} = useContext(CartContext)
	const [loading, setLoading] = useState(false)
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
	const {reactGA} = useContext(AnalyticsContext)
	const [noDiscountedBikesFound, setNoDiscountedBikesFound] = useState<boolean>(false)
	const [allowedPlanLengths, setAllowedPlanLengths] = useState<string[] | null>(["36", "48"])
	const [discountError, setDiscountError] = useState<{code:number, errorMessage:string}>({code: 0, errorMessage: ""})
	useEffect(() => {
		setData(prevData => {
			return {
				...prevData,
				discountCode: null,
				notApplicable: false,
				discount: 0,
				resultingPrice: 0
			}
		})
	}, [])
	if (organizationInitialized && organizationSlug && !discountCodeContext.isMandatoryForCheckout && !organizationData.isPublic) {
		return <></>
	}

	return (
		<>
			{newForm ?
				<>
					<div className="d-flex align-items-center mb-2 cursor-pointer"
						onClick={() => setIsFormOpen(prev => !prev)}>
						<Typography variant="bodySmBold"
							semanticTag="span"
							className="me-2">
							{t("Gib deinen Code hier ein.")}
						</Typography>
						<Image src={isFormOpen ? "/assets/icons/chevron-up-icon.svg" : "/assets/icons/chevron-down-icon.svg"}
							height={16}
							width={16}
							alt={"chevron"}
							objectFit="fill"/>
					</div>
					{data?.notApplicable && discountError?.code ? <div className={"alert alert-danger"}>
						<Typography variant="bodySm"
							semanticTag="span"
							className={"mb-4"}>
							{discountError?.code === 2 ? t(discountError?.errorMessage)
								.replace("x", allowedPlanLengths.slice(1).join(", "))
								.replace("z", allowedPlanLengths[0]) : discountError?.code === 3 ?
								t(discountError?.errorMessage).replace("x", allowedPlanLengths.join(", ")) : discountError?.code === 1 ?
									t(discountError?.errorMessage).replace("[organization]", organizationSlug) : t(discountError?.errorMessage)
							}
						</Typography>
					</div> : null}
					{noDiscountedBikesFound && <div className={"alert alert-danger"}>
						<Typography variant="bodySm"
							semanticTag="span"
							className={"mb-4"}>
							{t("no-discounted-products-message")}
						</Typography>
					</div>}
					<AccordionAnimation
						inProp={isFormOpen}
						duration={200}
						transTimingFn="ease-out"
						unmount>
						<div className={"col-12"}>
							<Formik
								initialValues={{
									discountCode: ""
								}}
								onSubmit={async (
									values: Values,
									{setSubmitting}: FormikHelpers<Values>
								) => {
									// Check discount code and calculate total price
									let totalPrice = 0
									const skusAndPrice = []
									items?.forEach(item => {
										// Only apply discount if bike is not on sale
										skusAndPrice.push({sku: item.product.sku, price: item.product.finalPriceWithoutDownPayment || item.product.price})
										const isSelectedVariationOnSale = item?.product?.customInstanceDiscounts ? item?.product?.customInstanceDiscounts?.[organizationSlug] : Boolean(item?.product?.discount)
										if (!isSelectedVariationOnSale || discountCodeContext.isMandatoryForCheckout) {
											totalPrice += parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
										}
									})
									setLoading(true)
									const planLengthMap = customPlanLengthMap[process.env.NEXT_PUBLIC_DISCOUNTS_INSTANCE]
									const res = await fetch("/api/discountCode", {
										method: "POST",
										headers: {
											"Content-Type": "application/json"
										},
										body: JSON.stringify({
											discountCode: values.discountCode,
											price: totalPrice,
											skusAndPrice,
											planLength: planLengthMap[data.planLength],
											maxDiscountCodeAmount: organizationData?.maxDiscountCodeAmount ? organizationData?.maxDiscountCodeAmount : 0,
											organizationSlug: discountCodeContext?.isMandatoryForCheckout ? organizationSlug : null
										})
									})
									const {discountCode, discount, resultingPrice, notApplicable, noDiscountedBikesFound, allowedPlanLengths: validPlanLengths, isOrganizationDiscount, error} = await res.json()
									setNoDiscountedBikesFound(() => noDiscountedBikesFound || false)
									setAllowedPlanLengths(prev => validPlanLengths?.length ? validPlanLengths : prev)

									reactGA?.event({
										category: gaCategories.checkout,
										action: gaEvents.discountCode,
										label: `${gaEvents.discountCode} ${discountCode}`,
										nonInteraction: false
									})
									setData(prevData => {
										return {
											...prevData,
											discountCode,
											notApplicable,
											discount: parseFloat(discount),
											resultingPrice: parseFloat(resultingPrice)
										}
									})
									setDiscountError(() => error)
									setDiscountCode(prevData => ({
										...prevData,
										code: discountCode,
										notApplicable,
										discountAmount: parseFloat(discount),
										isOrganizationDiscount
									}))
									setIsSubmitted(true)
									setLoading(false)
									setSubmitting(false)
								}}
							>
								<Form onChange={() => {
									if (isSubmitted) {
										setIsSubmitted(false)
									}
								}}>
									<div className={"row"}>
										<div className={"col-5"}>
											<InputWithIcon name="discountCode"
												loading={loading}
												icon={data?.resultingPrice &&
													isSubmitted && !discountError.code ? <div style={{backgroundColor: "#212529", height: 24, width: 24}}
														className="d-flex align-items-center justify-content-center rounded">
														<Image src={"/assets/icons/checkmark-white.svg"}
															width={20}
															height={20}/></div> : isSubmitted ?
														<X size={24}
															color={"#ff0000"}/> : <div style={{backgroundColor: "rgba(33, 37, 41, 0.04)", height: 24, width: 24}}
															className="d-flex align-items-center justify-content-center rounded">
															<Image src={"/assets/icons/checkmark-white.svg"}
																width={20}
																height={20}/></div>}/>
										</div>
									</div>
									<AutoSubmit/>
								</Form>
							</Formik>
						</div>
					</AccordionAnimation>
				</> :
				<>
					<Typography variant="heading3"
						semanticTag="h3"
						className={"mb-4"}>
						{t("Du hast einen Rabattcode?")}
					</Typography>
					{data?.notApplicable && discountError?.code ? <div className={"alert alert-danger"}>
						<Typography variant="bodySm"
							semanticTag="span"
							className={"mb-4"}>
							{discountError?.code === 2 ? t(discountError?.errorMessage)
								.replace("x", allowedPlanLengths.slice(1).join(", "))
								.replace("z", allowedPlanLengths[0]) : discountError?.code === 3 ?
								t(discountError?.errorMessage).replace("x", allowedPlanLengths.join(", ")) : discountError?.code === 1 ?
									t(discountError?.errorMessage).replace("[organization]", organizationSlug) : t(discountError?.errorMessage)
							}
						</Typography>
					</div> : null}
					{noDiscountedBikesFound && <div className={"alert alert-danger"}>
						<Typography variant="bodySm"
							semanticTag="span"
							className={"mb-4"}>
							{t("no-discounted-products-message")}
						</Typography>
					</div>}
					<Formik
						initialValues={{
							discountCode: ""
						}}
						onSubmit={async (
							values: Values,
							{setSubmitting}: FormikHelpers<Values>
						) => {
							// Check discount code and calculate total price
							let totalPrice = 0
							const skusAndPrice = []
							items?.forEach(item => {
								// Only apply discount if bike is not on sale
								skusAndPrice.push({sku: item.product.sku, price: item.product.finalPriceWithoutDownPayment || item.product.price})
								const isSelectedVariationOnSale = item?.product?.customInstanceDiscounts ? item?.product?.customInstanceDiscounts?.[organizationSlug] : Boolean(item?.product?.discount)
								if (!isSelectedVariationOnSale || discountCodeContext.isMandatoryForCheckout) {
									totalPrice += parseFloat(item.product.finalPriceWithoutDownPayment || item.product.price)
								}
							})
							setLoading(true)
							const res = await fetch("/api/discountCode", {
								method: "POST",
								headers: {
									"Content-Type": "application/json"
								},
								body: JSON.stringify({
									discountCode: values.discountCode,
									price: totalPrice,
									skusAndPrice,
									planLength: data.planLength,
									maxDiscountCodeAmount: organizationData?.maxDiscountCodeAmount ? organizationData?.maxDiscountCodeAmount : 0,
									organizationSlug: discountCodeContext?.isMandatoryForCheckout ? organizationSlug : null
								})
							})
							const {discountCode, discount, resultingPrice, notApplicable, noDiscountedBikesFound, allowedPlanLengths: validPlanLengths, isOrganizationDiscount, error} = await res.json()
							setNoDiscountedBikesFound(() => noDiscountedBikesFound || false)
							setAllowedPlanLengths(prev => validPlanLengths?.length ? validPlanLengths : prev)
							reactGA?.event({
								category: gaCategories.checkout,
								action: gaEvents.discountCode,
								label: `${gaEvents.discountCode} ${discountCode}`,
								nonInteraction: false
							})
							setData(prevData => {
								return {
									...prevData,
									discountCode,
									notApplicable,
									discount: parseFloat(discount),
									resultingPrice: parseFloat(resultingPrice)
								}
							})
							setDiscountError(() => error)
							setDiscountCode(prevData => ({
								...prevData,
								code: discountCode,
								notApplicable,
								discountAmount: parseFloat(discount),
								isOrganizationDiscount
							}))
							setIsSubmitted(true)

							setLoading(false)
							setSubmitting(false)
						}}
					>
						<Form onChange={() => {
							if (isSubmitted) {
								setIsSubmitted(false)
							}
						}}>
							<div className={"row"}>
								<div className={"col-12"}>
									<InputWithIcon name="discountCode"
										placeholder={t("Gib deinen Code hier ein.")}
										loading={loading}
										icon={data?.resultingPrice && isSubmitted && !discountError?.code ? <Check size={22}
											color={"#23A455"}/> : isSubmitted && discountError?.code ? <X size={22}
											color={"#ff0000"}/> : <ArrowRight size={22}/>}/>
								</div>
							</div>
							<AutoSubmit/>
						</Form>
					</Formik>
				</>
			}
		</>
	)
}

export default DiscountForm
